/**
 * What a buyer calls a Global Health product (customer decision 2026-10-02, A.1):
 * there are three products — the WolfPack Global Health Membership, the WolfPack
 * Practitioner Membership and the DNA & Health SPECIAL — and a duration is an
 * OPTION of one product, never a product of its own. So the buyer reads
 * "WolfPack Global Health Membership — 12 weeks", never the internal product
 * names ("WolfPack Health Club (weekly)", "WolfPack Practitioner Network (yearly)").
 *
 * Every buyer-facing surface (direct checkout, wizard, success screen, dashboard)
 * builds its labels here, so they cannot drift apart.
 */

/** The three products on sale. Mirrors the backend's plan payload `product_key`. */
export type ProductKey = 'membership' | 'practitioner' | 'dna_health_special' | 'dna_family_special';

/** Fallback for a backend that does not send `product_key`, and for stored rows of
 *  retired rhythms (which still are "the membership" to the member who holds one). */
const PRODUCT_KEY_BY_CODE: Record<string, ProductKey> = {
  GH_CLUB_WEEKLY:         'membership',
  GH_CLUB_YEARLY:         'membership',
  GH_CLUB:                'membership',
  GH_CLUB_MONTHLY:        'membership',
  GH_CLUB_SPECIAL:        'membership',
  GH_PRACTITIONER_WEEKLY: 'practitioner',
  GH_PRACTITIONER_YEARLY: 'practitioner',
  GH_DNA_HEALTH_SPECIAL:  'dna_health_special',
  GH_DNA_FAMILY_SPECIAL:  'dna_family_special'
};

/** The duration each code stands for; weekly codes count `weeks`. */
type Duration = 'weeks' | 'year' | 'two_years' | 'monthly' | null;
const DURATION_BY_CODE: Record<string, Duration> = {
  GH_CLUB_WEEKLY:         'weeks',
  GH_PRACTITIONER_WEEKLY: 'weeks',
  GH_CLUB_YEARLY:         'year',
  GH_PRACTITIONER_YEARLY: 'year',
  GH_CLUB:                'year',
  GH_CLUB_MONTHLY:        'monthly',
  GH_CLUB_SPECIAL:        'two_years',
  // The SPECIAL is one package; its included membership year is a component.
  GH_DNA_HEALTH_SPECIAL:  null,
  GH_DNA_FAMILY_SPECIAL:  null
};

/** Display prices of the products on sale (USD). The website and the direct
 *  checkout quote them before an account exists; the wizard always shows the
 *  server's own amount (which differs on dev/staging, see paymentTestMode). */
export const PRODUCT_PRICES = {
  membership:   { week: 9.99,  year: 399 },
  practitioner: { week: 19.99, year: 799 },
  dna_health_special: { once: 2499 },
  // Invitation only — no voluntary money-back guarantee.
  dna_family_special: { once: 999 }
} as const;

/** The plan codes of a product, weekly first. */
export const PRODUCT_CODES: Record<ProductKey, { week?: string; year?: string; once?: string }> = {
  membership:         { week: 'GH_CLUB_WEEKLY', year: 'GH_CLUB_YEARLY' },
  practitioner:       { week: 'GH_PRACTITIONER_WEEKLY', year: 'GH_PRACTITIONER_YEARLY' },
  dna_health_special: { once: 'GH_DNA_HEALTH_SPECIAL' },
  dna_family_special: { once: 'GH_DNA_FAMILY_SPECIAL' }
};

/** The product a plan code belongs to, or null for retired protection plans and
 *  the retired $1,999 package. `fromServer` is the payload's `product_key`. */
export function productKeyOf(code: string | null | undefined, fromServer?: string | null): ProductKey | null {
  if (fromServer === 'membership' || fromServer === 'practitioner' || fromServer === 'dna_health_special' ||
      fromServer === 'dna_family_special') {
    return fromServer;
  }
  return code ? PRODUCT_KEY_BY_CODE[code] ?? null : null;
}

export function isWeeklyCode(code: string | null | undefined): boolean {
  return !!code && DURATION_BY_CODE[code] === 'weeks';
}

/** Minimal translate contract (TranslateService satisfies it). */
interface Translator {
  translate(key: string, params?: Record<string, unknown>): string;
}

/** The product's name: "WolfPack Global Health Membership". */
export function productName(t: Translator, code: string | null | undefined, fromServer?: string | null): string {
  const key = productKeyOf(code, fromServer);
  if (key) return t.translate(`gh_product.${key}.name`);
  if (!code) return '';
  if (code === 'GH_DNA_HEALTH') return t.translate('gh_product.retired.dna_health');
  // Retired protection plans keep their own names (WolfPack Shield, …).
  return t.translate(`funnel.plans.${code.replace(/^GH_/, '').toLowerCase()}.name`) || code;
}

/** The duration option: "12 weeks", "1 week", "1 year" — or '' for the SPECIAL. */
export function durationLabel(t: Translator, code: string | null | undefined, weeks?: number | null): string {
  const duration = code ? DURATION_BY_CODE[code] ?? null : null;
  switch (duration) {
    case 'weeks':
      if (!weeks) return t.translate('gh_product.duration.weekly');
      return weeks === 1
        ? t.translate('gh_product.duration.week_one')
        : t.translate('gh_product.duration.weeks', { weeks });
    case 'year':      return t.translate('gh_product.duration.year');
    case 'two_years': return t.translate('gh_product.duration.two_years');
    case 'monthly':   return t.translate('gh_product.duration.monthly');
    default:          return '';
  }
}

/** "WolfPack Global Health Membership — 12 weeks" (just the name when there is no
 *  duration to name, e.g. the SPECIAL). */
export function productLabel(
  t: Translator, code: string | null | undefined, weeks?: number | null, fromServer?: string | null
): string {
  const name = productName(t, code, fromServer);
  const duration = durationLabel(t, code, weeks);
  return duration ? t.translate('gh_product.label', { product: name, duration }) : name;
}
